import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner, EmptyState, Money, StatusBadge, fmtMoney, fmtCompact, DemoChip, Watermark,
  CHART_COLORS, ChartTooltip, tooltipChrome } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import { Headphones, ArrowLeftRight, ShieldCheck, TrendingUp, TrendingDown, Landmark, CircleDollarSign } from "lucide-react";

export default function Portfolio() {
  const navigate = useNavigate();
  const { user, reportingCcy } = useAuth();
  const [cid, setCid] = useState(null);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/customers").then((r) => {
      if (r.data.length) setCid(r.data[0].id);
      else setErr("No linked account yet.");
    }).catch(() => setErr("Unable to load your portfolio."));
  }, []);

  useEffect(() => {
    if (!cid) return;
    setD(null);
    api.get(`/customers/${cid}/360?reporting_ccy=${reportingCcy}`).then((r) => setD(r.data)).catch(() => setErr("Unable to load your portfolio."));
  }, [cid, reportingCcy]);

  if (err) return <EmptyState title={err} hint="Please complete onboarding first." />;
  if (!d) return <Spinner label="Loading your portfolio…" />;

  const c = d.customer;
  const gain = d.gain_30d_reporting;
  const gainUp = (gain ?? 0) >= 0;
  const alloc = (d.asset_groups || []).map((g) => ({ name: g.label, value: g.value_reporting }));
  const series = (d.valuation_series || []).map((p) => ({ date: p.date.slice(5), value: p.value_reporting }));
  const holdingsCount = (d.asset_groups || []).reduce((n, g) => n + g.holdings.length, 0);

  const firstName = (c.name || "").split(" ")[0];
  const today = new Date("2026-06-15");

  return (
    <div className="fade-up max-w-3xl mx-auto relative" data-testid="customer-portfolio-screen">
      <Watermark />
      {/* Greeting */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline-label text-gold mb-1">My Portfolio</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Welcome back, {firstName}</h1>
        </div>
        <StatusBadge status={c.onboarding_status} testid="portfolio-onboarding-status" />
      </div>

      {/* Hero card */}
      <div className="relative overflow-hidden rounded-2xl hero-gradient gold-hairline p-5 sm:p-7 text-white card-elev" data-testid="portfolio-hero">
        <div className="flex items-start justify-between">
          <div>
            <div className="overline-label text-white/60">Total Portfolio Value ({reportingCcy})</div>
            <div className="font-num font-bold mt-2 tracking-tight leading-tight break-words text-[clamp(1.5rem,8vw,2.25rem)]" data-testid="portfolio-total-aum"><Money value={d.aum_total_reporting} ccy={reportingCcy} /></div>
            <div className="text-xs text-white/60 mt-1">{holdingsCount} holdings · Valuation {d.valuation_date}</div>
          </div>
          <DemoChip className="!text-white/70 !bg-white/10 !border-white/20" />
        </div>
        {gain !== null && gain !== undefined && (
          <div className="mt-4 flex items-center gap-2">
            <span data-testid="portfolio-gain-pill"
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-num font-semibold ${gainUp ? "bg-emerald-400/20 text-emerald-200" : "bg-rose-400/20 text-rose-200"}`}>
              {gainUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
              {gainUp ? "+" : ""}{d.gain_30d_pct}% · {gainUp ? "+" : "−"}{fmtCompact(Math.abs(gain), reportingCcy)} (30d)
            </span>
          </div>
        )}
        {series.length > 1 && (
          <div className="mt-3 h-14 -mx-1" data-testid="portfolio-trend">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
                <defs>
                  <linearGradient id="heroFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C59B27" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#C59B27" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip content={<ChartTooltip ccy={reportingCcy} />} {...tooltipChrome} />
                <Area type="monotone" dataKey="value" stroke="#C59B27" strokeWidth={2} fill="url(#heroFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Button onClick={() => navigate("/service-requests")} data-testid="quick-action-request"
            className="bg-white/10 hover:bg-white/20 border border-white/15 text-white gap-2 min-h-[44px]">
            <Headphones className="h-4 w-4" /> Request Service
          </Button>
          <Button onClick={() => navigate("/bgiof")} data-testid="quick-action-bgiof"
            className="bg-gold hover:bg-[#B0881E] text-slate-950 gap-2 min-h-[44px]">
            <ArrowLeftRight className="h-4 w-4" /> New BGIOF
          </Button>
        </div>
      </div>

      {/* Asset allocation */}
      <Card className="p-6 card-elev mt-5" data-testid="asset-allocation-donut-chart">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold flex items-center gap-2"><CircleDollarSign className="h-4 w-4 text-gold" /> Asset Allocation</h3>
          <span className="text-xs text-muted-foreground">by value ({reportingCcy})</span>
        </div>
        {alloc.length === 0 ? <EmptyState title="No investment holdings" hint="Holdings appear here once onboarded." /> : (
          <div className="grid sm:grid-cols-2 items-center gap-4">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={alloc} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={54} outerRadius={82} paddingAngle={3}>
                  {alloc.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTooltip ccy={reportingCcy} />} {...tooltipChrome} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-2">
              {alloc.map((a, i) => {
                const pct = d.aum_total_reporting ? (a.value / d.aum_total_reporting * 100) : 0;
                return (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid="allocation-legend-row">
                    <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="flex-1 truncate">{a.name}</span>
                    <span className="font-num text-xs text-muted-foreground">{pct.toFixed(1)}%</span>
                    <span className="font-num font-semibold min-w-[5rem] text-right text-xs sm:text-sm" title={fmtMoney(a.value, reportingCcy)}><Money value={a.value} ccy={reportingCcy} /></span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Holdings accordion */}
      <Card className="p-6 card-elev mt-5">
        <h3 className="font-semibold flex items-center gap-2 mb-3"><Landmark className="h-4 w-4 text-gold" /> Holdings</h3>
        {(d.asset_groups || []).length === 0 ? <EmptyState title="No holdings yet" /> : (
          <Accordion type="single" collapsible defaultValue={d.asset_groups[0]?.asset_class} className="w-full">
            {d.asset_groups.map((g) => (
              <AccordionItem key={g.asset_class} value={g.asset_class} data-testid={`holding-group-${g.asset_class}`}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center justify-between w-full pr-2">
                    <span className="font-medium">{g.label}</span>
                    <span className="font-num font-semibold"><Money value={g.value_reporting} ccy={reportingCcy} /></span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2">
                    {g.holdings.map((h) => (
                      <div key={h.id} className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="holding-item">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{h.name}</div>
                          <div className="text-[11px] font-num text-muted-foreground">{h.folio} · {h.source}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">{h.units?.toLocaleString()} units</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-num font-semibold text-sm">{fmtMoney(h.value_reporting, reportingCcy)}</div>
                          <div className="text-[11px] font-num text-muted-foreground">{fmtMoney(h.value, h.value_ccy)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </Card>

      {/* Insurance — separate from AUM */}
      <Card className="mt-5 overflow-hidden border-amber-200 dark:border-amber-900/50" data-testid="insurance-section">
        <div className="bg-gold-soft/70 dark:bg-amber-950/30 px-6 py-3 border-b border-amber-200 dark:border-amber-900/50 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#B0881E]" />
          <h3 className="font-semibold text-[#8a6a14] dark:text-amber-300">Life &amp; Wealth Protection</h3>
          <span className="ml-auto text-[11px] text-[#8a6a14]/80 dark:text-amber-400/80">Separate from investment AUM</span>
        </div>
        <div className="p-6">
          {(d.insurance || []).length === 0 ? <EmptyState title="No insurance policies" /> : (
            <div className="space-y-3">
              {d.insurance.map((p, i) => {
                const due = p.premium_due_date ? new Date(p.premium_due_date) : null;
                const overdue = due && due < today;
                return (
                  <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-border/60 p-3" data-testid={`insurance-row-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] font-num text-muted-foreground">{p.policy_no} · {p.source}</div>
                      {p.premium_due_date && (
                        <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${overdue ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`}>
                          {overdue ? "Premium overdue" : "Next premium"} {p.premium_due_date} · {fmtMoney(p.premium, p.premium_ccy)} {p.premium_frequency ? `(${p.premium_frequency})` : ""}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] text-muted-foreground">Sum Assured</div>
                      <div className="font-num font-semibold text-sm">{fmtMoney(p.cover, p.value_ccy)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
